import React, { useEffect, useState, Fragment } from 'react';
import { Table, Spin, Button, DatePicker, message, Input, Select } from 'antd';
import { getFtaOppDataService, getFtaOppReportSummaryService } from '../../../services/FTAReportService';
import { getAllAdminIECsService } from '../../../services/FileDataService';
import { getBRCorelationService } from '../../../services/MasterService';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { Excel } from 'antd-table-saveas-excel';
import { Col, Row } from 'react-bootstrap';
import Card from '../../../components/bootstrap/card';
import { getTodaysDate, formatToTwoDecimals } from '../../../utilities/utils';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import ReportDateFilter from '../../../components/ReportDateFilter';

import useColumnVisibility from '../../../hooks/useColumnVisibility';
import ColumnSelector from '../../../components/common/ColumnSelector';

const { RangePicker } = DatePicker;
const FtaOppReport = () => {
    // const iec_no = localStorage.getItem('iec_no');
    const [jsonData, setJsonData] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [summaryData, setSummaryData] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [selectedIECName, setSelectedIECName] = useState(localStorage.getItem('name'))
    const [isValid, setIsValid] = useState(true); // Validation state
    const [billNo, setBillNo] = useState("");
    const [branchCodeNames, setBranchCodeNames] = useState([]);
    const [selectedBranchCodes, setSelectedBranchCodes] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("ALL");
    // The remark filter that the currently-shown data/summary were fetched with.
    // Only updated when data is (re)queried (Submit / initial load) so the
    // on-page summary and the detail table always reflect the same filter.
    const [appliedCategoryFilter, setAppliedCategoryFilter] = useState("ALL");
    const [cooCosFilter, setCooCosFilter] = useState("ALL");
    // The COO=COS filter the currently-shown data/summary were fetched with,
    // updated only when data is (re)queried — same pattern as appliedCategoryFilter.
    const [appliedCooCosFilter, setAppliedCooCosFilter] = useState("ALL");

    const location = useLocation();
    const currentYear = dayjs().year();
    const defaultFrom = dayjs().startOf('year');
    const defaultTo = dayjs().endOf('year');
    const [dates, setDates] = useState(() => {
        // if report is opened through navigation from dashboard, then use those dates else default
        if (location.state?.startDate && location.state?.endDate) {
            return [dayjs(location.state.startDate), dayjs(location.state.endDate)];
        }
        if (location.state?.passedDates?.length === 2) {
            return [dayjs(location.state.passedDates[0]), dayjs(location.state.passedDates[1])];
        }
        return [defaultFrom, defaultTo];
    });

    const [IECOptions, setIECOptions] = useState([]);
    const userRole = localStorage.getItem('role');
    const [selectedIEC, setSelectedIEC] = useState(
        localStorage.getItem('iec_no')
    );

    // ─── TABLE STYLES ────────────────────────────────────────────────────────────
    const tableStyles = `
        .fta-opp-table .ant-table-thead > tr > th {
            white-space: normal !important;
            word-break: break-word !important;
            overflow: visible !important;
            text-overflow: unset !important;
            vertical-align: middle !important;
            line-height: 1.3 !important;
            padding-top: 8px !important;
            padding-bottom: 8px !important;
        }
        .fta-opp-table .ant-table-tbody > tr > td {
            white-space: nowrap;
        }
        .fta-opp-table .ant-table-container {
            overflow-x: auto !important;
        }
        .fta-opp-table .ant-table-header {
            overflow: hidden !important;
        }
        .fta-opp-table .ant-table-sticky-holder {
            overflow: hidden !important;
        }
    `;

    // Define columns for the table
    const columns = [
        {
            title: 'BUSINESS UNIT',
            dataIndex: 'business_unit',
            key: 'business_unit',
            header: 'BUSINESS UNIT',
            width: 160,
            fixed: 'left',
        },
        {
            title: 'PLANT NAME',
            dataIndex: 'plant_name',
            key: 'plant_name',
            header: 'PLANT NAME',
            width: 150,
            fixed: 'left',
        },
        {
            title: 'MONTH',
            dataIndex: 'month_start',
            key: 'month_start',
            header: 'MONTH',
            width: 90
        },
        {
            title: 'ENTITY',
            dataIndex: 'entity',
            key: 'entity',
            header: 'ENTITY',
            width: 110
        },
        {
            title: 'ORG. CODE',
            dataIndex: 'org_code',
            key: 'org_code',
            header: 'ORG. CODE',
            width: 130
        },
        {
            title: 'IEC/BR',
            dataIndex: 'iec_br',
            key: 'iec_br',
            header: 'IEC/BR',
            width: 150
        },
        {
            title: 'PORT CODE',
            dataIndex: 'port_code',
            key: 'port_code',
            header: 'PORT CODE',
            width: 115
        },
        {
            title: 'FY',
            dataIndex: 'fy',
            key: 'fy',
            header: 'FY',
            width: 80
        },
        {
            title: 'BOE NO.',
            dataIndex: 'be_no',
            key: 'be_no',
            header: 'BOE NO.',
            width: 115
        },
        {
            title: 'BOE DT',
            dataIndex: 'be_date',
            key: 'be_date',
            header: 'BOE DT',
            width: 110,
            render: (text, record) => {
                if (record.be_date) {
                    return formatDateToDDMMYY(record.be_date);
                }
            }
        },
        {
            title: 'REF CODE.',
            dataIndex: 'reference_code',
            key: 'reference_code',
            header: 'REF CODE.',
            width: 115
        },
        {
            title: 'ORIGIN CODE',
            dataIndex: 'origin_code',
            key: 'origin_code',
            header: 'ORIGIN CODE',
            width: 135
        },
        {
            title: 'ORIGIN COUNTRY',
            dataIndex: 'country_of_origin',
            key: 'country_of_origin',
            header: 'ORIGIN COUNTRY',
            width: 160
        },
        {
            title: 'CONSIGNMENT CODE',
            dataIndex: 'consignment_code',
            key: 'consignment_code',
            header: 'CONSIGNMENT CODE',
            width: 185
        },
        {
            title: 'CONSIGNMENT COUNTRY',
            dataIndex: 'country_of_consignment',
            key: 'country_of_consignment',
            header: 'CONSIGNMENT COUNTRY',
            width: 210
        },
        {
            title: 'COO AND COS',
            dataIndex: 'coo_cos',
            key: 'coo_cos',
            header: 'COO AND COS',
            width: 135,
            render: (text, record) => {
                return text == true ? 'TRUE' : 'FALSE';
            }
        },
        {
            title: 'HSN CODE',
            dataIndex: 'cth_item_detail',
            key: 'cth_item_detail',
            header: 'HSN CODE',
            width: 115
        },
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            header: 'DESCRIPTION',
            width: 130
        },
        {
            title: 'ASSE. VALUE IN INR',
            dataIndex: 'clean_assess_value',
            key: 'clean_assess_value',
            header: 'ASSE. VALUE IN INR',
            align: 'right',
            width: 185,
            render: (text, record) => {
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'BCD RATE',
            dataIndex: 'clean_bcd_rate',
            key: 'clean_bcd_rate',
            header: 'BCD RATE',
            align: 'right',
            width: 110,
            render: (text, record) => {
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'ITEM DUTY (All)',
            dataIndex: 'item_duty',
            key: 'item_duty',
            header: 'ITEM DUTY (All)',
            align: 'right',
            width: 155,
            render: (text, record) => {
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'BCD NTF. NO.',
            dataIndex: 'bcd_notn_no',
            key: 'bcd_notn_no',
            header: 'BCD NTF. NO.',
            width: 135
        },
        {
            title: 'BCD NTF. SR. NO.',
            dataIndex: 'bcd_notn_sno',
            key: 'bcd_notn_sno',
            header: 'BCD NTF. SR. NO.',
            width: 165
        },
        {
            title: 'BCD AMT',
            dataIndex: 'bcd_amount',
            key: 'bcd_amount',
            header: 'BCD AMT',
            align: 'right',
            width: 110,
            render: (text, record) => {
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'EXEMPTED BCD (INR)',
            dataIndex: 'exempted_bcd',
            key: 'exempted_bcd',
            header: 'EXEMPTED BCD (INR)',
            align: 'right',
            width: 165,
            render: (text, record) => {
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'FTA BCD RATE',
            dataIndex: 'clean_fta_rate',
            key: 'clean_fta_rate',
            header: 'FTA BCD RATE',
            align: 'right',
            width: 140,
            render: (text, record) => {
                // FTA is not applicable → no FTA rate exists; show blank instead of 0%.
                // Display-only: the view still uses 0 for applied_duty/duty_saving.
                if (record.category_remark === 'Not Applicable') {
                    return null;
                }
                // FTA BCD RATE is stored as a fraction (0.06 = 6%), so scale to a
                // true percentage for display.
                return formatToTwoDecimals(parseFloat(text) * 100) + '%'
            }
        },
        {
            title: 'APPLI. DUTY',
            dataIndex: 'applied_duty',
            key: 'applied_duty',
            header: 'APPLI. DUTY',
            align: 'right',
            width: 130,
            render: (text, record) => {
                // Mirror FTA BCD RATE: blank when "Not Applicable"; otherwise show
                // the value, including a genuine 0.
                if (record.category_remark === 'Not Applicable') {
                    return null;
                }
                return formatToTwoDecimals(text)
            }
        },
        {
            title: 'BCD SAVING(Cumulative Cess & IGST is not considered)',
            dataIndex: 'duty_saving',
            key: 'duty_saving',
            header: 'BCD SAVING(Cumulative Cess & IGST is not considered)',
            align: 'right',
            width: 480,
            render: (text, record) => {
                if (record.category_remark === 'Not Applicable') {
                    return null;
                }
                return formatToTwoDecimals(text);
            }
        },
        {
            title: 'BCD SAVING (FTA Not Applicable)',
            dataIndex: 'duty_saving_na',
            key: 'duty_saving_na',
            header: 'BCD SAVING (FTA Not Applicable)',
            align: 'right',
            width: 290,
            render: (text, record) => {
                if (record.category_remark === 'Not Applicable') {
                    return formatToTwoDecimals(record.duty_saving);
                }
                return null;
            }
        },
        {
            title: 'NOTI. NO. & DATE',
            dataIndex: 'notification_no',
            key: 'notification_no',
            header: 'NOTI. NO. & DATE',
            width: 175
        },
        {
            title: 'ITEM SR. NO.',
            dataIndex: 'item_sr_no',
            key: 'item_sr_no',
            header: 'ITEM SR. NO.',
            align: 'right',
            width: 135
        },
        {
            title: 'AGREEMENT',
            dataIndex: 'agreement',
            key: 'agreement',
            header: 'AGREEMENT',
            width: 125
        },
        {
            title: 'CATEGORY REMARKS',
            dataIndex: 'category_remark',
            key: 'category_remark',
            header: 'CATEGORY REMARKS',
            width: 180
        },
        {
            title: 'SCHEME CODE',
            dataIndex: 'scheme_code',
            key: 'scheme_code',
            header: 'SCHEME CODE',
            width: 130
        }
    ];

    const { visibleKeys, setVisibleKeys, filteredColumns, resetToDefault } =
        useColumnVisibility('FTAOppReport', columns);

    const fetchData = async () => {
        const filters = {
            from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
            to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
            bill_no: billNo || null,
            br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
            category_remark: categoryFilter !== "ALL" ? categoryFilter : null,
            coo_cos: cooCosFilter !== "ALL" ? cooCosFilter : null,
        };
        setLoading(true);
        try {
            const cleanIEC = selectedIEC?.split(';')[0];
            const response = await getFtaOppDataService(cleanIEC, filters);

            // When a row has no business unit, show "Unknown" (applies to both the
            // table and the Excel export, which reads from jsonData).
            const normalized = (response || []).map((row) => ({
                ...row,
                business_unit: row.business_unit || 'Unknown',
            }));
            setJsonData(normalized);
            if (response && response.length > 0) {
                message.success(`Loaded ${response.length} records.`);
            } else {
                message.warning("No records found for the selected criteria.");
            }
        } catch (error) {
            setError('Error fetching FTA data.');
        } finally {
            setLoading(false);
        }
    };

    const fetchSummary = async () => {
        const filters = {
            from_date: dates?.[0] ? dayjs(dates[0]).format('YYYY-MM-DD') : null,
            to_date: dates?.[1] ? dayjs(dates[1]).format('YYYY-MM-DD') : null,
            br_code: selectedBranchCodes.length > 0 ? selectedBranchCodes : null,
            coo_cos: cooCosFilter !== "ALL" ? cooCosFilter : null,
            category_remark: categoryFilter !== "ALL" ? categoryFilter : null,
        };
        setSummaryLoading(true);
        try {
            const cleanIEC = selectedIEC?.split(';')[0];
            const response = await getFtaOppReportSummaryService(cleanIEC, filters);
            setSummaryData(Array.isArray(response) ? response : []);
        } catch (error) {
            message.error("Error fetching FTA Opportunity summary.");
        } finally {
            setSummaryLoading(false);
        }
    };

    useEffect(() => {
        if (userRole === 'superadmin' || userRole === 'group_head') {
            getAdminIEC();
        }
    }, []);

    useEffect(() => {
        if (selectedIEC) {
            const cleanIEC = selectedIEC?.split(';')[0];
            // Fetch branches first
            getBRCorelationService(cleanIEC).then((response) => {
                setBranchCodeNames(response);
                // After branches are loaded, fetch data + summary together so the
                // page has everything on load and Export doesn't need another call
                setAppliedCategoryFilter(categoryFilter);
                setAppliedCooCosFilter(cooCosFilter);
                fetchData();
                fetchSummary();
            });
        }
    }, [selectedIEC]);


    function formatDateToDDMMYY(dateString) {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return ''; // Return empty string if the dateString is not a valid date
        }
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        return `${day}/${month}/${year}`;
    }

    const getDateOnly = (dateString) => {
        if (dateString && dateString.length !== 0) {
            const date = new Date(dateString);
            return date;
        }
        else {
            return '';
        }
    }

    const handleDateChange = (value) => {
        setDates(value || []);
        if (value && value.length === 2) {
            const from_date = value[0];
            const to_date = value[1];

            // Validate that both dates are selected
            if (!from_date || !to_date) {
                setIsValid(false);
                return;
            }

            // Validate that the duration is within 1 year
            const oneYearLater = from_date.add(1, 'year');
            if (to_date.isAfter(oneYearLater)) {
                message.error("The selected duration should not exceed 1 year.");
                setIsValid(false);
                return;
            }

            setIsValid(true);
        } else {
            setIsValid(false);
        }
    };

    const getAdminIEC = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await getAllAdminIECsService(token);

            if (response?.data) {
                const options = response.data.map((item, index) => ({
                    value: `${item.iec_no};${item.fullname}`,
                    label: `${item.iec_no} (${item.fullname})`,
                    key: index,
                }));
                setIECOptions(options);
            }
        } catch (err) {
            message.error("Failed to load IEC list");
        }
    };

    const handleSubmit = () => {
        if (!selectedIEC) {
            message.error("Please select an IEC");
            return;
        }

        if (dates.length === 2 && isValid) {
            setAppliedCategoryFilter(categoryFilter);
            setAppliedCooCosFilter(cooCosFilter);
            fetchData();
            fetchSummary();
        } else {
            message.error("Please select a valid date range");
        }
    };

    const handleIECSelection = (val) => {
        if (val) {
            const [iec_no, iec_name] = val.split(";");
            setSelectedIEC(iec_no);
            setSelectedIECName(iec_name);
        } else {
            setSelectedIEC(null);
            setSelectedIECName('');
        }
    };

    const handleBillSearch = (value) => {
        setBillNo(value);
    };

    const handleBranchCodeSelection = (values) => {
        setSelectedBranchCodes(values);
    };

    const exportToExcel = async () => {
        let workbook = new ExcelJS.Workbook();

        // ── Sheet 1: Dashboard Summary ──────────────────────────────────────────
        // Reuses the summary already loaded on page load/Submit - no API call here.
        try {
            const summaryRows = summaryData;

            const summarySheet = workbook.addWorksheet('Summary');

            // 1. Write the raw data to hidden columns (AA to AF) on the same sheet.
            // AF carries coo_cos as a NON-boolean token ('T'/'F'/'U') so the SUMIFS
            // below can slice on it and NULL coo_cos is only counted under "All".
            //
            // We must NOT store (or match on) the literal words 'TRUE'/'FALSE':
            // Excel and Google Sheets coerce a SUMIFS/COUNTIF criterion of "TRUE"
            // or "FALSE" into the boolean value, which then only matches actual
            // boolean cells — never the text "TRUE"/"FALSE" — so the sum collapses
            // to 0. (Mac Numbers doesn't coerce, which is why it "worked" there.)
            // Using 'T'/'F'/'U' avoids the reserved words on both sides entirely.
            const cooCosToken = (v) => (v === true ? 'T' : v === false ? 'F' : 'U');
            summarySheet.getCell('AA1').value = 'entity';
            summarySheet.getCell('AB1').value = 'category_remark';
            summarySheet.getCell('AC1').value = 'total_eligible';
            summarySheet.getCell('AD1').value = 'bcd_availed';
            summarySheet.getCell('AE1').value = 'bcd_missed';
            summarySheet.getCell('AF1').value = 'coo_cos';

            const uniqueEntities = new Set();
            const uniqueRemarks = new Set();

            summaryRows.forEach((row, idx) => {
                const r = idx + 2;
                // Fall back to "Unknown" when entity is missing — otherwise no entity
                // rows are emitted and the Grand Total range collapses to an invalid
                // (inverted) SUM that Excel shows as an error.
                const entity = row.entity || 'Unknown';
                summarySheet.getCell(`AA${r}`).value = entity;
                summarySheet.getCell(`AB${r}`).value = row.category_remark;
                summarySheet.getCell(`AC${r}`).value = row.total_eligible != null ? parseFloat(row.total_eligible) : 0;
                summarySheet.getCell(`AD${r}`).value = row.bcd_availed != null ? parseFloat(row.bcd_availed) : 0;
                summarySheet.getCell(`AE${r}`).value = row.bcd_missed_opp != null ? parseFloat(row.bcd_missed_opp) : 0;
                summarySheet.getCell(`AF${r}`).value = cooCosToken(row.coo_cos);

                uniqueEntities.add(entity);
                if (row.category_remark) uniqueRemarks.add(row.category_remark);
            });

            // Hide the raw data columns (AA to AF)
            for (let c = 27; c <= 32; c++) {
                summarySheet.getColumn(c).hidden = true;
            }

            // 2. Add Filter UI at the top
            // Default the filter cell to the remark the report was queried with,
            // so the exported summary matches the on-page summary and Data sheet.
            // ("ALL" maps to "All" — the value the SUMIFS formulae check for.)
            summarySheet.getCell('A1').value = 'Category Remark Filter:';
            summarySheet.getCell('A1').font = { bold: true };
            summarySheet.getCell('B1').value =
                appliedCategoryFilter === 'ALL' ? 'All'
                    : appliedCategoryFilter === 'FTA Applicable' ? 'All - FTA Applicable'
                        : appliedCategoryFilter;
            summarySheet.getCell('B1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F0FF' } };

            // COO = COS filter cell — a real dropdown (All / TRUE / FALSE), defaulting
            // to the value the report was queried with. ("ALL" maps to "All" — the
            // value the SUMIFS formulae check for.)
            summarySheet.getCell('D1').value = 'COO = COS Filter:';
            summarySheet.getCell('D1').font = { bold: true };
            summarySheet.getCell('E1').value = appliedCooCosFilter === 'ALL' ? 'All' : appliedCooCosFilter;
            summarySheet.getCell('E1').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F0FF' } };
            // Force plain text on the COO=COS filter cell. Without this, Excel and
            // Google Sheets auto-convert the words TRUE/FALSE into the boolean
            // value the moment they land in the cell — which (a) no longer matches
            // the text items in the validation list ("Input must fall within
            // specified range"), and (b) makes the mapping formula below always
            // fall through to FALSE. '@' keeps E1 as the text "TRUE"/"FALSE".
            summarySheet.getCell('E1').numFmt = '@';

            // Write unique values to hidden column AG for data validation
            // This prevents issues with commas, spaces, and the 255-character limit in string-based data validations
            // "All - FTA Applicable" is a synthetic entry (both FTA Applicable
            // buckets); the SUMIFS below translates it to the wildcard "FTA Applicable*".
            const remarksArray = ['All', 'All - FTA Applicable', ...Array.from(uniqueRemarks).filter(Boolean)];
            remarksArray.forEach((val, idx) => {
                summarySheet.getCell(`AG${idx + 1}`).value = val;
            });
            summarySheet.getColumn('AG').hidden = true;

            // COO=COS dropdown options in hidden column AH. These must also be plain
            // text — if the source list items TRUE/FALSE coerce to booleans, the
            // validation list holds booleans while the selected cell holds text (or
            // vice-versa) and nothing matches.
            const cooCosArray = ['All', 'TRUE', 'FALSE'];
            cooCosArray.forEach((val, idx) => {
                const cell = summarySheet.getCell(`AH${idx + 1}`);
                cell.numFmt = '@';
                cell.value = val;
            });
            summarySheet.getColumn('AH').hidden = true;

            summarySheet.getCell('B1').dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`$AG$1:$AG$${remarksArray.length}`]
            };

            summarySheet.getCell('E1').dataValidation = {
                type: 'list',
                allowBlank: false,
                formulae: [`$AH$1:$AH$${cooCosArray.length}`]
            };

            // 3. Define Table Headers at Row 3
            const headers = ['Entity', 'Total Eligible', 'BCD Availed', 'BCD Missed Opp.', 'Percentage Availed', 'Percentage Missed'];
            const headerRow = summarySheet.getRow(3);
            headers.forEach((hdr, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = hdr;
                cell.font = { bold: true };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC000' } };
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });

            // Set visible column widths
            summarySheet.columns = [
                { width: 25 }, { width: 18 }, { width: 18 }, { width: 20 }, { width: 20 }, { width: 20 }
            ];

            // 4. Add dynamic rows for each Entity
            let currentRow = 4;
            Array.from(uniqueEntities).sort().forEach(entity => {
                // Dynamically SUM by entity, and by the B1 (category) + E1 (coo_cos)
                // filters. A filter set to "All" becomes the "*" wildcard, which
                // matches every non-empty text value in that column — so "All"
                // includes every category / coo_cos (NULL coo_cos is stored as the
                // token 'U', so it is matched by "*" but not by 'T'/'F').
                //
                // E1 shows the human-readable words All/TRUE/FALSE, but the criterion
                // is mapped to the non-boolean tokens 'T'/'F' that AF actually stores.
                // Matching on the literal words "TRUE"/"FALSE" would be coerced to a
                // boolean by Excel/Google Sheets and never match the text cells.
                // OR($E$1="TRUE",$E$1=TRUE) matches whether E1 stayed text "TRUE" or
                // got coerced to the boolean TRUE, so the mapping to the 'T'/'F'
                // tokens in AF is correct regardless of how the app treats the cell.
                const buildSumifs = (col) => `SUMIFS(${col}:${col}, AA:AA, $A${currentRow}, AB:AB, IF($B$1="All","*",IF($B$1="All - FTA Applicable","FTA Applicable*",$B$1)), AF:AF, IF($E$1="All","*",IF(OR($E$1="TRUE",$E$1=TRUE),"T","F")))`;

                summarySheet.getCell(`A${currentRow}`).value = entity;
                summarySheet.getCell(`B${currentRow}`).value = { formula: buildSumifs('AC') }; // Total Eligible
                summarySheet.getCell(`C${currentRow}`).value = { formula: buildSumifs('AD') }; // BCD Availed
                summarySheet.getCell(`D${currentRow}`).value = { formula: buildSumifs('AE') }; // BCD Missed

                // Percentages
                summarySheet.getCell(`E${currentRow}`).value = { formula: `IF(B${currentRow}=0, 0, C${currentRow}/B${currentRow})` };
                summarySheet.getCell(`E${currentRow}`).numFmt = '0.00%';

                summarySheet.getCell(`F${currentRow}`).value = { formula: `IF(B${currentRow}=0, 0, D${currentRow}/B${currentRow})` };
                summarySheet.getCell(`F${currentRow}`).numFmt = '0.00%';

                currentRow++;
            });

            // Add Grand Total row
            summarySheet.getCell(`A${currentRow}`).value = 'Grand Total';
            summarySheet.getCell(`A${currentRow}`).font = { bold: true };

            // First entity row is row 4; guard against an inverted range (SUM(x4:x3))
            // when there are no entity rows, which Excel reports as an error.
            const lastEntityRow = currentRow - 1;
            ['B', 'C', 'D'].forEach(col => {
                summarySheet.getCell(`${col}${currentRow}`).value = {
                    formula: lastEntityRow >= 4 ? `SUM(${col}4:${col}${lastEntityRow})` : '0'
                };
                summarySheet.getCell(`${col}${currentRow}`).font = { bold: true };
            });

            summarySheet.getCell(`E${currentRow}`).value = { formula: `IF(B${currentRow}=0, 0, C${currentRow}/B${currentRow})` };
            summarySheet.getCell(`E${currentRow}`).numFmt = '0.00%';
            summarySheet.getCell(`E${currentRow}`).font = { bold: true };

            summarySheet.getCell(`F${currentRow}`).value = { formula: `IF(B${currentRow}=0, 0, D${currentRow}/B${currentRow})` };
            summarySheet.getCell(`F${currentRow}`).numFmt = '0.00%';
            summarySheet.getCell(`F${currentRow}`).font = { bold: true };

            currentRow++;

            // Format number columns
            ['B', 'C', 'D'].forEach(col => {
                for (let i = 4; i < currentRow; i++) {
                    summarySheet.getCell(`${col}${i}`).numFmt = '#,##0.00';
                }
            });

            // Highlighted warning so users don't delete the helper columns/rows
            // (AA–AH) that the Category Remark and COO=COS filters read from via
            // SUMIFS + data validation. Written both below the visible summary
            // table and right beside the helper block so it's seen either way.
            const warningText =
                '⚠ IMPORTANT: Do NOT delete or edit the helper columns AA–AH (and their rows) on this sheet. ' +
                'The Category Remark and COO = COS filters above read from them — deleting these columns or rows will break the Summary filters.';
            const warnFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            const warnFont = { bold: true, color: { argb: 'FF9C0006' } };
            const warnBorder = {
                top: { style: 'medium', color: { argb: 'FFBF8F00' } },
                bottom: { style: 'medium', color: { argb: 'FFBF8F00' } },
                left: { style: 'medium', color: { argb: 'FFBF8F00' } },
                right: { style: 'medium', color: { argb: 'FFBF8F00' } },
            };
            const paintNote = (startCell, endCell) => {
                summarySheet.mergeCells(`${startCell}:${endCell}`);
                const cell = summarySheet.getCell(startCell);
                cell.value = warningText;
                cell.fill = warnFill;
                cell.font = warnFont;
                cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
                cell.border = warnBorder;
            };
            
            // 2. Beside the helper block (column AI is not hidden, so it shows
            //    right after the AA–AH columns even when they are collapsed).
            paintNote('AI1', 'AN4');
            summarySheet.getColumn('AI').width = 24;

        } catch (err) {
            console.error('Failed to load dashboard summary for export:', err);
            message.warning('Dashboard Summary sheet could not be loaded — exporting without it.');
        }

        // ── Sheet 2: Detailed Data ──────────────────────────────────────────────
        let worksheet = workbook.addWorksheet('Data');
        var data = jsonData;

        worksheet.columns = filteredColumns.map(col => ({
            header: col.header || col.title,
            key: col.dataIndex,
            width: 20
        }));
        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFC000' },
            };
            cell.alignment = { vertical: 'middle' };
        });

        data.forEach((obj) => {
            const isNotApplicable = obj.category_remark === 'Not Applicable';
            worksheet.addRow({
                ...obj,
                coo_cos: obj.coo_cos === true || obj.coo_cos === 'true' || obj.coo_cos === 'TRUE' ? 'TRUE' : 'FALSE',
                be_date: getDateOnly(obj.be_date),
                leo_date: getDateOnly(obj.leo_date),
                bcd_rate: obj.bcd_rate ? parseFloat(obj.bcd_rate) : '',
                bcd_amount: obj.bcd_amount ? parseFloat(obj.bcd_amount) : '',
                exempted_bcd: obj.exempted_bcd != null ? parseFloat(obj.exempted_bcd) : '',
                // FTA not applicable → leave FTA BCD RATE empty (mirrors the table render).
                // Display-only; the view's applied_duty/duty_saving are unaffected.
                clean_fta_rate: isNotApplicable
                    ? null : (obj.clean_fta_rate != null ? parseFloat(obj.clean_fta_rate) : null),
                // Mirror FTA BCD RATE: blank only when "Not Applicable" (no FTA
                // rate). Otherwise show the value — including a genuine 0 (which
                // 0 = assess * 0% would previously blank via the falsy check).
                applied_duty: isNotApplicable
                    ? null : (obj.applied_duty != null ? parseFloat(obj.applied_duty) : null),
                assess_value: obj.assess_value ? parseFloat(obj.assess_value) : '',
                // Mirror the render logic: use duty_saving_na value for "Not Applicable" rows, duty_saving otherwise
                duty_saving: isNotApplicable
                    ? null : (obj.duty_saving != null ? parseFloat(obj.duty_saving) : null),
                duty_saving_na: !isNotApplicable
                    ? null : (obj.duty_saving != null ? parseFloat(obj.duty_saving) : null),
            });
        });

        // Limit all amount/rate columns to 2 decimals in the export. The cells are
        // written as raw floats (e.g. applied_duty = assess * fta_rate yields tails
        // like 36601.445999999996), so without a number format Excel shows the full
        // precision — unlike the on-page table, which renders every one of these via
        // toFixed(2). '0.00' makes the export match the UI (display-only; the stored
        // value is untouched, so any downstream math keeps full precision).
        const twoDecimalCols = [
            'clean_assess_value', 'item_duty', 'bcd_amount',
            'exempted_bcd', 'applied_duty', 'duty_saving', 'duty_saving_na',
        ];
        twoDecimalCols.forEach((key) => {
            const col = worksheet.getColumn(key);
            if (col) col.numFmt = '0.00';
        });
        // Both rate columns display as percentages, but on different scales:
        //  - BCD RATE is already a percent number (7.5 = 7.5%), so append a
        //    literal '%' with 0.00"%".
        //  - FTA BCD RATE is stored as a fraction (0.06 = 6%), so use Excel's
        //    0.00% which multiplies by 100 -> 6.00%.
        {
            const bcdCol = worksheet.getColumn('clean_bcd_rate');
            if (bcdCol) bcdCol.numFmt = '0.00"%"';
            const ftaCol = worksheet.getColumn('clean_fta_rate');
            if (ftaCol) ftaCol.numFmt = '0.00%';
        }

        // Generate Excel file
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Lucrative's CIP DSS - FTA Opportunities as on ${getTodaysDate()} of ${selectedIEC}.xlsx`);
    };

    // Does a row's category_remark satisfy the selected Category filter?
    // "FTA Applicable" is a synthetic bucket meaning both FTA Applicable
    // categories (Availed + Not Availed), i.e. everything except Not Applicable.
    const categoryMatches = (remark, filter) => {
        if (filter === 'ALL') return true;
        if (filter === 'FTA Applicable') return typeof remark === 'string' && remark.startsWith('FTA Applicable');
        return remark === filter;
    };

    // Normalise a row's coo_cos (boolean, possibly NULL) to the filter's vocabulary.
    // NULL is its own bucket so it is only counted under "ALL" — matching the detail
    // query, where coo_cos IS TRUE / IS FALSE both exclude NULL rows.
    const cooCosKey = (v) => (v === true ? 'TRUE' : v === false ? 'FALSE' : 'UNKNOWN');

    // Rolls the raw entity+category+coo_cos summary rows up to one row per entity
    // (plus a Grand Total), for the on-page summary widget.
    const summaryByEntity = (() => {
        const map = {};
        // Respect BOTH the Category Remark and COO=COS filters. The summary API
        // returns one row per entity+category_remark+coo_cos, so we roll up only the
        // rows matching the selected filters (or all rows when "ALL").
        summaryData
            .filter((row) => categoryMatches(row.category_remark, appliedCategoryFilter)
                && (appliedCooCosFilter === 'ALL' || cooCosKey(row.coo_cos) === appliedCooCosFilter))
            .forEach((row) => {
            const e = row.entity || 'Unknown';
            if (!map[e]) map[e] = { entity: e, total_eligible: 0, bcd_availed: 0, bcd_missed_opp: 0 };
            map[e].total_eligible += Number(row.total_eligible) || 0;
            map[e].bcd_availed += Number(row.bcd_availed) || 0;
            map[e].bcd_missed_opp += Number(row.bcd_missed_opp) || 0;
        });
        const rows = Object.values(map).map((r) => ({
            ...r,
            availed_percentage: r.total_eligible ? (r.bcd_availed / r.total_eligible) * 100 : 0,
            missed_percentage: r.total_eligible ? (r.bcd_missed_opp / r.total_eligible) * 100 : 0,
        }));
        if (rows.length === 0) return rows;
        const grand = rows.reduce(
            (acc, r) => ({
                entity: 'Grand Total',
                total_eligible: acc.total_eligible + r.total_eligible,
                bcd_availed: acc.bcd_availed + r.bcd_availed,
                bcd_missed_opp: acc.bcd_missed_opp + r.bcd_missed_opp,
            }),
            { entity: 'Grand Total', total_eligible: 0, bcd_availed: 0, bcd_missed_opp: 0 }
        );
        grand.availed_percentage = grand.total_eligible ? (grand.bcd_availed / grand.total_eligible) * 100 : 0;
        grand.missed_percentage = grand.total_eligible ? (grand.bcd_missed_opp / grand.total_eligible) * 100 : 0;
        return [...rows, grand];
    })();

    const summaryColumns = [
        {
            title: 'ENTITY', dataIndex: 'entity', key: 'entity', width: 140,
            render: (text, record) => record.entity === 'Grand Total' ? <b>{text}</b> : text,
        },
        {
            title: 'TOTAL ELIGIBLE', dataIndex: 'total_eligible', key: 'total_eligible', align: 'right', width: 170,
            render: (v, record) => {
                const t = formatToTwoDecimals(v);
                return record.entity === 'Grand Total' ? <b>{t}</b> : t;
            },
        },
        {
            title: 'BCD AVAILED', dataIndex: 'bcd_availed', key: 'bcd_availed', align: 'right', width: 160,
            render: (v, record) => {
                const t = formatToTwoDecimals(v);
                return record.entity === 'Grand Total' ? <b>{t}</b> : t;
            },
        },
        {
            title: 'BCD MISSED OPP.', dataIndex: 'bcd_missed_opp', key: 'bcd_missed_opp', align: 'right', width: 170,
            render: (v, record) => {
                const t = formatToTwoDecimals(v);
                return record.entity === 'Grand Total' ? <b>{t}</b> : t;
            },
        },
        {
            title: '% AVAILED', dataIndex: 'availed_percentage', key: 'availed_percentage', align: 'right', width: 120,
            render: (v, record) => {
                const t = `${formatToTwoDecimals(v)}%`;
                return record.entity === 'Grand Total' ? <b>{t}</b> : t;
            },
        },
        {
            title: '% MISSED', dataIndex: 'missed_percentage', key: 'missed_percentage', align: 'right', width: 120,
            render: (v, record) => {
                const t = `${formatToTwoDecimals(v)}%`;
                return record.entity === 'Grand Total' ? <b>{t}</b> : t;
            },
        },
    ];

    return (
        <Fragment>
            <style>{tableStyles}</style>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between">
                            <div className="header-title">
                                <h4 className="card-title">Lucrative's CIP DSS - (New) FTA Opportunities as on {moment().format('DD/MM/YYYY')} of {selectedIECName}</h4>
                                <p><i>Note: Subject to FTA/PTA & DGFT conditions fulfilment.</i></p>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                                <ColumnSelector
                                    allColumns={columns}
                                    visibleKeys={visibleKeys}
                                    setVisibleKeys={setVisibleKeys}
                                    onReset={resetToDefault}
                                />
                                <button
                                    className='btn btn-primary cip-btn-submit'
                                    onClick={exportToExcel}
                                >
                                    Export to Excel
                                </button>
                            </div>
                        </Card.Header>
                        <Card.Body className="d-flex justify-content-start align-items-center gap-2 ps-4">
                            {(userRole === 'superadmin' || userRole === 'group_head') && (
                                <Select
                                    showSearch
                                    placeholder="Select IEC"
                                    onChange={handleIECSelection}
                                    allowClear
                                    options={IECOptions}
                                    style={{ width: '300px' }}
                                />
                            )}
                            <ReportDateFilter dates={dates} onChange={handleDateChange} />
                            <Input
                                placeholder="Enter Bill No"
                                allowClear
                                value={billNo}
                                onChange={(e) => handleBillSearch(e.target.value)}
                                style={{ width: 250 }}
                            />
                            <Select
                                mode="multiple"
                                allowClear
                                style={{ width: 250 }}
                                placeholder="Select Branch Code"
                                value={selectedBranchCodes}
                                onChange={handleBranchCodeSelection}
                                options={branchCodeNames.map((item) => ({
                                    value: item.branch_code,
                                    label: `${item.branch_code} / ${item.plant_name} (${item.business_unit})`,
                                }))}
                            />
                            <Select
                                allowClear
                                style={{ width: 250 }}
                                placeholder="Select Category"
                                value={categoryFilter}
                                onChange={(val) => setCategoryFilter(val || "ALL")}
                                options={[
                                    { value: 'ALL', label: 'ALL' },
                                    // "All - FTA Applicable" = both FTA Applicable buckets
                                    // (Availed + Not Availed), i.e. everything except Not Applicable.
                                    { value: 'FTA Applicable', label: 'All - FTA Applicable' },
                                    { value: 'FTA Applicable and Availed', label: 'FTA Applicable and Availed' },
                                    { value: 'FTA Applicable but Not Availed', label: 'FTA Applicable but Not Availed' },
                                    { value: 'Not Applicable', label: 'Not Applicable' },
                                ]}
                            />
                            <Select
                                allowClear
                                style={{ width: 200 }}
                                placeholder="Select COO = COS"
                                value={cooCosFilter}
                                onChange={(val) => setCooCosFilter(val || "ALL")}
                                options={[
                                    { value: 'ALL', label: 'COO = COS: ALL' },
                                    { value: 'TRUE', label: 'COO = COS: TRUE' },
                                    { value: 'FALSE', label: 'COO = COS: FALSE' },
                                ]}
                            />
                            <Button type="primary" className="cip-btn-submit"
                                onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Card.Body>
                        <Card.Body className="px-4 pt-0 pb-0">
                            <h5 className="mb-2">Summary</h5>
                            <Table
                                className="fta-opp-summary-table"
                                bordered
                                size="small"
                                columns={summaryColumns}
                                dataSource={summaryByEntity}
                                rowKey="entity"
                                loading={summaryLoading}
                                pagination={false}
                                scroll={{ x: 'max-content' }}
                            />
                        </Card.Body>
                        <Card.Body className="px-0 report-page">
                            <Table
                                className="fta-opp-table"
                                bordered
                                columns={filteredColumns}
                                dataSource={jsonData}
                                loading={isLoading}
                                scroll={{ x: 'max-content' }}
                                
                                pagination={{ showTotal: (total) => `Total ${total} records`, showSizeChanger: true }}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default FtaOppReport;
